import { create } from 'zustand';
import { remiWs, clearSessionId } from '../socket/remi';
import { disposeGraphqlWsClient } from '../socket/graphql';
import type { RemiAsyncJob, RemiConnectionStatus, RemiJobStatusMessage, ToolUseIndicator } from '../socket/remi';
import { normalizeKpiBlocks, type KpiBlock } from '../types/remiBlocks';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  role: 'user' | 'remi';
  content: string;
  isStreaming?: boolean;
  kpiBlocks?: KpiBlock[];
}

interface AskRemiState {
  // Chat
  messages: ChatMessage[];
  isLoading: boolean;
  isModalOpen: boolean;
  asyncJobs: RemiAsyncJob[];

  // Streaming / connection
  connectionStatus: RemiConnectionStatus;
  streamingContent: string;
  activeToolUse: ToolUseIndicator | null;
  error: string | null;

  // Actions — chat
  sendMessage: (content: string) => void;
  useBackgroundJobInChat: (job: RemiAsyncJob) => void;
  dismissAsyncJob: (jobTaskId: string) => void;
  openModal: () => void;
  closeModal: () => void;
  clearMessages: () => void;

  // Actions — streaming internals (called by WS callbacks)
  appendStreamChunk: (chunk: string) => void;
  finalizeResponse: (content: string, uiBlocks?: unknown[]) => void;
  setToolUse: (indicator: ToolUseIndicator | null) => void;
  setConnectionStatus: (status: RemiConnectionStatus) => void;
  setError: (error: string | null) => void;
  applyJobStatus: (msg: RemiJobStatusMessage) => void;

  // Actions — connection lifecycle
  connect: () => Promise<void>;
  disconnect: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

interface QueuedTurn {
  content: string;
  context?: {
    backgroundJobs?: RemiAsyncJob[];
    backgroundJobFollowup?: boolean;
  };
}

let idCounter = 0;
function nextId(): string {
  return `msg-${Date.now()}-${++idCounter}`;
}

function jobPreview(job: RemiAsyncJob): Record<string, unknown> {
  const artifactPreview = job.artifact?.preview_json;
  if (artifactPreview && typeof artifactPreview === 'object') {
    return artifactPreview as Record<string, unknown>;
  }
  if (job.result && typeof job.result === 'object') {
    return job.result;
  }
  return {};
}

function formatPreviewRow(row: unknown): string {
  if (!row || typeof row !== 'object') return String(row ?? '');
  return Object.entries(row as Record<string, unknown>)
    .slice(0, 4)
    .map(([key, value]) => `${key}=${String(value)}`)
    .join(', ');
}

function buildJobMessage(job: RemiAsyncJob): string {
  const label = job.jobType === 'remi_query_export' ? 'Background export' : 'Background query';
  if (job.status === 'failed') {
    return `${label} failed${job.message ? `: ${job.message}` : '.'}`;
  }
  if (job.status === 'canceled') {
    return `${label} was canceled.`;
  }

  const preview = jobPreview(job);
  const rowCount = typeof preview.row_count === 'number' ? preview.row_count : undefined;
  const rows = Array.isArray(preview.preview_rows) ? preview.preview_rows.slice(0, 3) : [];

  if (job.jobType === 'remi_query_export') {
    return `${label} completed${rowCount !== undefined ? `: ${rowCount.toLocaleString()} rows` : ''}. The download link is in the background card.`;
  }

  const rowSummary = rows.map(formatPreviewRow).filter(Boolean).join(' | ');
  return `${label} completed${rowCount !== undefined ? `: ${rowCount.toLocaleString()} rows` : ''}.${rowSummary ? ` Preview: ${rowSummary}` : ''}`;
}

function buildJobPullForwardPrompt(job: RemiAsyncJob): string {
  const label = job.jobType === 'remi_query_export' ? 'background export' : 'background query';
  if (job.status === 'failed') {
    return `Use this failed ${label} as context. Explain what failed, whether anything useful was produced, and what we should do next.`;
  }
  if (job.status === 'canceled') {
    return `Use this canceled ${label} as context. Explain what was canceled and what I can do next.`;
  }
  return `Use this completed ${label} as context. Summarize the result and help me continue from it.`;
}

function shouldAppendJobMessage(existing: RemiAsyncJob | undefined, next: RemiAsyncJob): boolean {
  if (!['completed', 'failed', 'canceled'].includes(next.status)) return false;
  return existing?.status !== next.status;
}

function shouldAutoContinueJob(existing: RemiAsyncJob | undefined, next: RemiAsyncJob): boolean {
  if (!['completed', 'failed'].includes(next.status)) return false;
  return existing?.status !== next.status;
}

function shouldKeepJobCard(job: RemiAsyncJob): boolean {
  return ['queued', 'running', 'completed', 'failed'].includes(job.status);
}

// rAF debounce for high-frequency stream updates
let rafId: number | null = null;
let pendingContent = '';

// ─── Strict client-side serialization ────────────────────────────────────────
// Only ONE message is in the WS pipeline at a time. When a user sends while
// a response is streaming, the message goes into outboundQueue and is held
// until the current turn fully finalizes or errors. This guarantees:
//   • Every WS callback belongs to exactly one in-flight turn (no ambiguity)
//   • No need for per-turn identity tagging from the backend
//   • No shared mutable state between concurrent turns (there are none)
const outboundQueue: QueuedTurn[] = []; // turns waiting to be sent

function flushStream(set: (partial: Partial<AskRemiState> | ((s: AskRemiState) => Partial<AskRemiState>)) => void) {
  const content = pendingContent;
  rafId = null;
  set((state) => {
    const msgs = [...state.messages];
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'remi' && msgs[i].isStreaming) {
        msgs[i] = { ...msgs[i], content };
        break;
      }
    }
    return { messages: msgs };
  });
}

/** Send the next queued message. Called after a turn finalizes or errors. */
function drainQueue(
  set: (partial: Partial<AskRemiState> | ((s: AskRemiState) => Partial<AskRemiState>)) => void,
  _get: () => AskRemiState,
) {
  if (outboundQueue.length === 0) return;
  const next = outboundQueue.shift()!;

  if (next.context) {
    remiWs.sendContext(next.context);
  }

  const remiMsg: ChatMessage = {
    id: nextId(),
    role: 'remi',
    content: '',
    isStreaming: true,
  };

  pendingContent = '';
  set((s) => ({
    messages: [...s.messages, remiMsg],
    isLoading: true,
    streamingContent: '',
    activeToolUse: null,
    error: null,
  }));

  remiWs.sendChat(next.content);
}

function startHiddenTurn(
  content: string,
  context: QueuedTurn['context'],
  set: (partial: Partial<AskRemiState> | ((s: AskRemiState) => Partial<AskRemiState>)) => void,
  get: () => AskRemiState,
) {
  if (get().isLoading) {
    outboundQueue.push({ content, context });
    return;
  }

  if (context) {
    remiWs.sendContext(context);
  }

  const remiMsg: ChatMessage = {
    id: nextId(),
    role: 'remi',
    content: '',
    isStreaming: true,
  };

  pendingContent = '';
  set((state) => ({
    messages: [...state.messages, remiMsg],
    isLoading: true,
    streamingContent: '',
    activeToolUse: null,
    error: null,
  }));

  remiWs.sendChat(content);
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useAskRemiStore = create<AskRemiState>((set, get) => ({
  // Initial state
  messages: [],
  isLoading: false,
  isModalOpen: false,
  asyncJobs: [],
  connectionStatus: 'disconnected',
  streamingContent: '',
  activeToolUse: null,
  error: null,

  // ── Chat actions ─────────────────────────────────────────────────────────

  sendMessage: async (content: string) => {
    const state = get();

    // Lazy connect
    if (state.connectionStatus !== 'connected') {
      await state.connect();
    }

    // If a response is in flight, queue the message — it will be sent after
    // the current turn finalizes. User bubble shows immediately.
    if (state.isLoading) {
      const userMsg: ChatMessage = { id: nextId(), role: 'user', content };
      outboundQueue.push({ content });
      set((s) => ({ messages: [...s.messages, userMsg] }));
      return;
    }

    // No response in flight — send immediately
    const userMsg: ChatMessage = { id: nextId(), role: 'user', content };
    const remiMsg: ChatMessage = {
      id: nextId(),
      role: 'remi',
      content: '',
      isStreaming: true,
    };

    pendingContent = '';
    set({
      messages: [...get().messages, userMsg, remiMsg],
      isLoading: true,
      streamingContent: '',
      activeToolUse: null,
      error: null,
    });

    remiWs.sendChat(content);
  },

  useBackgroundJobInChat: async (job: RemiAsyncJob) => {
    if (get().connectionStatus !== 'connected') {
      await get().connect();
    }
    set((state) => ({
      asyncJobs: state.asyncJobs.filter((item) => item.jobTaskId !== job.jobTaskId),
    }));
    startHiddenTurn(
      buildJobPullForwardPrompt(job),
      { backgroundJobs: [job], backgroundJobFollowup: true },
      set,
      get,
    );
  },

  dismissAsyncJob: (jobTaskId: string) => {
    set((state) => ({
      asyncJobs: state.asyncJobs.filter((job) => job.jobTaskId !== jobTaskId),
    }));
  },

  openModal: () => set((state) => ({
    isModalOpen: true,
    asyncJobs: state.asyncJobs.filter(shouldKeepJobCard),
  })),
  closeModal: () => set({ isModalOpen: false }),

  clearMessages: () => {
    remiWs.disconnect();
    clearSessionId();
    disposeGraphqlWsClient();
    pendingContent = '';
    outboundQueue.length = 0;
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    set({
      messages: [],
      isLoading: false,
      streamingContent: '',
      activeToolUse: null,
      error: null,
      asyncJobs: [],
      connectionStatus: 'disconnected',
    });
  },

  // ── Streaming internals ──────────────────────────────────────────────────

  appendStreamChunk: (chunk: string) => {
    if (!get().isLoading) return; // No active turn
    pendingContent += chunk;
    set({ streamingContent: pendingContent });

    // Debounce DOM updates to one per frame
    if (!rafId) {
      rafId = requestAnimationFrame(() => flushStream(set));
    }
  },

  finalizeResponse: (content: string, uiBlocks?: unknown[]) => {
    if (!get().isLoading) return; // No active turn — stale callback

    // Cancel any pending rAF
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    pendingContent = '';

    const kpiBlocks = normalizeKpiBlocks(uiBlocks);

    set((state) => {
      const msgs = [...state.messages];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'remi' && msgs[i].isStreaming) {
          msgs[i] = {
            ...msgs[i],
            content,
            isStreaming: false,
            kpiBlocks: kpiBlocks.length > 0 ? kpiBlocks : undefined,
          };
          break;
        }
      }
      return {
        messages: msgs,
        isLoading: false,
        streamingContent: '',
        activeToolUse: null,
      };
    });

    // If there are queued messages, send the next one
    drainQueue(set, get);
  },

  setToolUse: (indicator) => set({ activeToolUse: indicator }),
  setConnectionStatus: (status) => set({ connectionStatus: status }),
  setError: (error) => set({ error, isLoading: false }),
  applyJobStatus: (msg) => {
    const existingJob = get().asyncJobs.find((job) => job.jobTaskId === msg.job_task_id);
    const nextJob: RemiAsyncJob = {
      jobTaskId: msg.job_task_id,
      conversationId: msg.conversation_id,
      jobType: msg.job_type,
      status: msg.status,
      message: msg.message,
      summary: msg.summary,
      artifact: msg.artifact ?? null,
      result: msg.result ?? null,
    };
    const autoContinue = shouldAutoContinueJob(existingJob, nextJob);

    set((state) => {
      const existing = state.asyncJobs.filter((job) => job.jobTaskId !== msg.job_task_id);
      const appendMessage = shouldAppendJobMessage(existingJob, nextJob) && !autoContinue;
      const keepCard = shouldKeepJobCard(nextJob);

      return {
        asyncJobs: keepCard ? [nextJob, ...existing].slice(0, 5) : existing,
        messages: appendMessage
          ? [...state.messages, { id: nextId(), role: 'remi', content: buildJobMessage(nextJob) }]
          : state.messages,
      };
    });

    if (autoContinue) {
      startHiddenTurn(
        buildJobPullForwardPrompt(nextJob),
        { backgroundJobs: [nextJob], backgroundJobFollowup: true },
        set,
        get,
      );
    }
  },

  // ── Connection lifecycle ─────────────────────────────────────────────────

  connect: async () => {
    const store = get();

    // Wire callbacks
    remiWs.onStatusChange = (status) => store.setConnectionStatus(status);
    remiWs.onStreamChunk = (chunk) => get().appendStreamChunk(chunk);
    remiWs.onMessageComplete = (content, uiBlocks) => get().finalizeResponse(content, uiBlocks);

    remiWs.onToolUse = (msg) => {
      get().setToolUse(
        msg.status === 'starting'
          ? { tool: msg.tool, status: msg.status, label: msg.label }
          : null
      );
    };

    remiWs.onActionComplete = () => {
      // Future: invalidate React Query caches here
    };

    remiWs.onError = (content) => {
      if (!get().isLoading) return; // No active turn — stale callback

      const s = get();
      const msgs = [...s.messages];
      let found = false;
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'remi' && msgs[i].isStreaming) {
          msgs[i] = {
            ...msgs[i],
            content: content || 'Something went wrong. Please try again.',
            isStreaming: false,
          };
          found = true;
          break;
        }
      }
      if (found) {
        set({ messages: msgs, isLoading: false, activeToolUse: null, error: content });
      } else {
        set({ error: content, isLoading: false });
      }

      // If there are queued messages, send the next one
      drainQueue(set, get);
    };

    remiWs.onSessionStart = () => {
      // Treat the post-init session event as the authoritative "Live" signal.
      get().setConnectionStatus('connected');
    };

    remiWs.onJobStatus = (msg) => {
      get().applyJobStatus(msg);
    };

    await remiWs.connect();
  },

  disconnect: () => {
    remiWs.disconnect();
    disposeGraphqlWsClient();
    set({ connectionStatus: 'disconnected' });
  },
}));
